import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notificationApi, type NotificationInboxItemDto, type UserRole } from "../../lib/api";
import { subscribeRealtimeEvent, type RealtimeEvent } from "../../lib/realtime";
import type {
  ProviderNotificationInboxState,
  ProviderNotificationPaginationState,
  ProviderNotificationPreferencesState,
} from "./ProviderNotificationInboxView";
import type { ProviderNotificationServerItem } from "./provider-notification-data";
import { resolveProviderNotificationTarget } from "./provider-notification-target";

/** Legacy panel shape retained for existing presentation-only callers/tests. */
export interface LegacyProviderNotificationItem {
  id: string;
  type: RealtimeEvent["type"];
  customerName: string | null;
  createdAtMs: number;
  read: boolean;
  muted: boolean;
  targetPath: "/provider/floor" | "/provider/schedule";
  targetSearch: string;
  venueId: string | null;
  entityId: string | null;
}

export type ProviderNotificationItem = LegacyProviderNotificationItem | ProviderNotificationServerItem;

export interface ProviderNotificationIdentity {
  userId: string | null;
  providerId: string | null;
  role: UserRole | null;
}

export function filterProviderNotificationsByVenue<T extends ProviderNotificationItem>(notifications: T[], activeVenueId: string | null): T[] {
  if (!activeVenueId) return notifications;
  return notifications.filter((notification) => {
    if ("dto" in notification) return notification.dto.target.venueId === activeVenueId || notification.dto.target.venueId === undefined;
    return notification.venueId === null || notification.venueId === activeVenueId;
  });
}

function mapServerItem(dto: NotificationInboxItemDto, identity: ProviderNotificationIdentity): ProviderNotificationServerItem {
  return {
    id: dto.id,
    createdAtMs: Date.parse(dto.created_at),
    read: dto.read_at !== null,
    category: dto.category,
    eventType: dto.event_type,
    target: resolveProviderNotificationTarget(dto.target, {
      expiresAt: dto.expires_at,
      providerId: identity.providerId,
      role: identity.role,
    }),
    dto,
  };
}

function mergePage(current: ProviderNotificationServerItem[], page: ProviderNotificationServerItem[]) {
  const seen = new Set(current.map((item) => item.id));
  return [...current, ...page.filter((item) => !seen.has(item.id))];
}

const INITIAL_PREFERENCES: ProviderNotificationPreferencesState = { status: "loading" };
const INITIAL_INBOX_STATE: ProviderNotificationInboxState = { status: "loading" };
const INITIAL_PAGINATION: ProviderNotificationPaginationState = { nextCursor: null, status: "idle" };

export function useProviderNotifications(
  enabled: boolean,
  activeVenueId: string | null = null,
  identity: ProviderNotificationIdentity = { userId: null, providerId: null, role: null },
) {
  const identityKey = enabled && identity.userId ? `${identity.userId}:${identity.providerId ?? ""}:${identity.role ?? ""}` : null;
  const [items, setItems] = useState<ProviderNotificationServerItem[]>([]);
  const [inboxState, setInboxState] = useState<ProviderNotificationInboxState>(INITIAL_INBOX_STATE);
  const [pagination, setPagination] = useState<ProviderNotificationPaginationState>(INITIAL_PAGINATION);
  const [preferences, setPreferences] = useState<ProviderNotificationPreferencesState>(INITIAL_PREFERENCES);
  const generationRef = useRef(0);
  const preferencesGenerationRef = useRef(0);
  // State updates happen after render. Keep its owner separate from the live
  // identity so an account/provider change never paints the prior snapshot.
  const snapshotIdentityKeyRef = useRef<string | null>(identityKey);
  const currentIdentityKeyRef = useRef<string | null>(identityKey);
  currentIdentityKeyRef.current = identityKey;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const [serverUnreadCount, setServerUnreadCount] = useState(0);
  const [readBusy, setReadBusy] = useState(false);
  const [readError, setReadError] = useState(false);
  const readOperationRef = useRef<{ ids: Set<string>; all: boolean } | null>(null);
  const readTaskRef = useRef<object | null>(null);
  const applyPendingReads = (incoming: ProviderNotificationServerItem[]) => incoming.map(item =>
    readOperationRef.current?.ids.has(item.id) ? { ...item, read: true } : item);

  const preferenceInFlightRef = useRef(false);

  const isCurrentSnapshot = useCallback((generation: number, key: string | null) => (
    generation === generationRef.current &&
    key !== null &&
    key === currentIdentityKeyRef.current &&
    key === snapshotIdentityKeyRef.current
  ), []);

  const isCurrentPreferencesSnapshot = useCallback((generation: number, key: string | null) => (
    generation === preferencesGenerationRef.current &&
    key !== null &&
    key === currentIdentityKeyRef.current &&
    key === snapshotIdentityKeyRef.current
  ), []);

  const loadFirst = useCallback(async (generation: number, key: string, refreshing: boolean) => {
    if (!isCurrentSnapshot(generation, key)) return;
    setInboxState({ status: refreshing ? "refreshing" : "loading" });
    setPagination({ nextCursor: null, status: "idle" });
    try {
      const response = await notificationApi.listInbox();
      if (!isCurrentSnapshot(generation, key)) return;
      const incoming = response.notifications.map((item) => mapServerItem(item, identityRef.current));
      setItems(applyPendingReads(incoming));
      setServerUnreadCount(readOperationRef.current?.all ? 0 : Math.max(0, response.unread_count - incoming.filter(item => !item.read && readOperationRef.current?.ids.has(item.id)).length));
      setPagination({ nextCursor: response.next_cursor, status: "idle" });
      setInboxState({ status: "ready" });
      return response;
    } catch {
      if (isCurrentSnapshot(generation, key)) setInboxState({ status: "error" });
    }
  }, [isCurrentSnapshot]);

  const loadPreferences = useCallback(async (generation: number, key: string) => {
    if (!isCurrentPreferencesSnapshot(generation, key)) return;
    setPreferences({ status: "loading" });
    try {
      const response = await notificationApi.getPreferences();
      if (isCurrentPreferencesSnapshot(generation, key)) setPreferences({ status: "ready", values: response.preferences });
    } catch {
      if (isCurrentPreferencesSnapshot(generation, key)) setPreferences({ status: "error" });
    }
  }, [isCurrentPreferencesSnapshot]);

  const reload = useCallback(() => {
    if (!identityKey || currentIdentityKeyRef.current !== identityKey || snapshotIdentityKeyRef.current !== identityKey) return;
    const generation = ++generationRef.current;
    void loadFirst(generation, identityKey, items.length > 0);
  }, [identityKey, items.length, loadFirst]);

  const reloadPreferences = useCallback(() => {
    if (!identityKey || currentIdentityKeyRef.current !== identityKey || snapshotIdentityKeyRef.current !== identityKey) return;
    const generation = ++preferencesGenerationRef.current;
    void loadPreferences(generation, identityKey);
  }, [identityKey, loadPreferences]);

  useEffect(() => {
    const generation = ++generationRef.current;
    readOperationRef.current = null;
    readTaskRef.current = null;
    setReadBusy(false);
    setReadError(false);
    setServerUnreadCount(0);
    preferenceInFlightRef.current = false;
    snapshotIdentityKeyRef.current = identityKey;
    const preferencesGeneration = ++preferencesGenerationRef.current;
    if (!identityKey) {
      setItems([]);
      setInboxState(INITIAL_INBOX_STATE);
      setPagination(INITIAL_PAGINATION);
      setPreferences(INITIAL_PREFERENCES);
      return;
    }
    setItems([]);
    void loadFirst(generation, identityKey, false);
    void loadPreferences(preferencesGeneration, identityKey);
  }, [identityKey, loadFirst, loadPreferences]);

  useEffect(() => {
    if (!identityKey) return;
    return subscribeRealtimeEvent(() => reload());
  }, [identityKey, reload]);

  const loadMore = useCallback(async (cursor: string) => {
    if (!identityKey || !isCurrentSnapshot(generationRef.current, identityKey) || pagination.status === "loading" || pagination.nextCursor !== cursor) return;
    const generation = generationRef.current;
    setPagination({ nextCursor: cursor, status: "loading" });
    try {
      const response = await notificationApi.listInbox(cursor);
      if (!isCurrentSnapshot(generation, identityKey)) return;
      setItems((current) => mergePage(current, applyPendingReads(response.notifications.map((item) => mapServerItem(item, identityRef.current)))));
      if (!readOperationRef.current) setServerUnreadCount(response.unread_count);
      setPagination({ nextCursor: response.next_cursor, status: "idle" });
    } catch {
      if (isCurrentSnapshot(generation, identityKey)) setPagination({ nextCursor: cursor, status: "error" });
    }
  }, [identityKey, isCurrentSnapshot, pagination]);

  const markRead = useCallback(async (notificationId?: string): Promise<boolean> => {
    if (!identityKey || !isCurrentSnapshot(generationRef.current, identityKey) || readTaskRef.current) return false;
    const ids = new Set(items.filter(item => !item.read && (!notificationId || item.id === notificationId)).map(item => item.id));
    if (notificationId && ids.size === 0) return true;
    if (!notificationId && serverUnreadCount === 0 && ids.size === 0) return true;
    const operation = { ids, all: !notificationId };
    const priorUnreadCount = serverUnreadCount;
    readOperationRef.current = operation;
    readTaskRef.current = operation;
    setReadBusy(true);
    setReadError(false);
    setItems(current => current.map(item => ids.has(item.id) ? { ...item, read: true } : item));
    setServerUnreadCount(count => notificationId ? Math.max(0, count - 1) : 0);
    const isCurrent = () => readTaskRef.current === operation && currentIdentityKeyRef.current === identityKey;
    let saved = false;
    try {
      if (notificationId) await notificationApi.markRead(notificationId);
      else {
        const updatedCount = await notificationApi.markAllRead();
        if (isCurrent()) setServerUnreadCount(Math.max(0, priorUnreadCount - updatedCount));
      }
      if (!isCurrent()) return false;
      saved = true;
    } catch {
      if (!isCurrent()) return false;
      setItems(current => current.map(item => ids.has(item.id) ? { ...item, read: false } : item));
      setServerUnreadCount(priorUnreadCount);
      setReadError(true);
    }
    if (!isCurrent()) return false;
    // A write outlives any number of GET refreshes. Invalidate older responses,
    // then confirm the server snapshot (including read-all's bounded batch).
    readOperationRef.current = null;
    const generation = ++generationRef.current;
    if (notificationId && saved) {
      // A confirmed individual read should not drop older loaded pages or move
      // the user's place in All. The successful POST is authoritative for it.
      setItems(current => current.map(item => ids.has(item.id) ? { ...item, read: true } : item));
      setInboxState({ status: "ready" });
      setPagination(current => ({ ...current, status: "idle" }));
    } else {
      const confirmed = await loadFirst(generation, identityKey, true);
      if (!saved && confirmed && isCurrent() && (notificationId
        ? confirmed.notifications.some(item => item.id === notificationId && item.read_at !== null)
        : confirmed.unread_count === 0)) {
        saved = true;
        setReadError(false);
      }
    }
    if (isCurrent()) {
      readTaskRef.current = null;
      setReadBusy(false);
    }
    return saved;
  }, [identityKey, isCurrentSnapshot, items, loadFirst, serverUnreadCount]);

  const markNotificationRead = useCallback((notificationId: string) => markRead(notificationId), [markRead]);
  const markAllNotificationsRead = useCallback(() => markRead(), [markRead]);

  const updatePreference = useCallback((category: ProviderNotificationServerItem["category"], enabledValue: boolean) => {
    if (!identityKey || !isCurrentSnapshot(generationRef.current, identityKey) || preferenceInFlightRef.current || preferences.status === "loading") return;
    const values = preferences.values;
    if (!values || values[category] === enabledValue) return;
    const generation = preferencesGenerationRef.current;
    preferenceInFlightRef.current = true;
    setPreferences({ status: "saving", savingCategory: category, values: { ...values, [category]: enabledValue } });
    void notificationApi.updatePreferences({ [category]: enabledValue }).then((response) => {
      if (isCurrentPreferencesSnapshot(generation, identityKey)) setPreferences({ status: "ready", values: response.preferences });
    }).catch(() => {
      if (isCurrentPreferencesSnapshot(generation, identityKey)) setPreferences({ status: "error", values });
    }).finally(() => {
      if (isCurrentPreferencesSnapshot(generation, identityKey)) preferenceInFlightRef.current = false;
    });
  }, [identityKey, isCurrentSnapshot, isCurrentPreferencesSnapshot, preferences]);

  const markNotificationTargetUnavailable = useCallback((notificationId: string, reason: "stale" | "unauthorized" | "invalid") => {
    if (!identityKey || !isCurrentSnapshot(generationRef.current, identityKey)) return;
    setItems((current) => current.map((item) => item.id === notificationId ? { ...item, target: { availability: "unavailable", reason } } : item));
  }, [identityKey, isCurrentSnapshot]);

  const visibleNotifications = useMemo(() => filterProviderNotificationsByVenue(items, activeVenueId), [activeVenueId, items]);
  const unreadCount = activeVenueId ? visibleNotifications.filter(item => !item.read).length : serverUnreadCount;
  const hasCurrentSnapshot = identityKey !== null && snapshotIdentityKeyRef.current === identityKey;

  return {
    inboxState: hasCurrentSnapshot ? inboxState : INITIAL_INBOX_STATE,
    loadMore, markAllNotificationsRead, markNotificationRead,
    readBusy: hasCurrentSnapshot && readBusy, readError: hasCurrentSnapshot && readError,
    markNotificationTargetUnavailable, notifications: hasCurrentSnapshot ? visibleNotifications : [],
    pagination: hasCurrentSnapshot ? pagination : INITIAL_PAGINATION,
    preferences: hasCurrentSnapshot ? preferences : INITIAL_PREFERENCES,
    reload, reloadPreferences, unreadCount: hasCurrentSnapshot ? unreadCount : 0, updatePreference,
  };
}
